import { useCallback, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslations } from '@fenetech/translations';
import useSalespersons from 'helpers/context/SelectionValues/useSalespersons';
import { IEmployee, IQuotePermissions } from "helpers/interfaces";
import useAddQuoteData from "../AddQuote/useAddQuoteData";
import useQuoteData from "../useQuoteData";
import { QuoteRequiredFieldEnum } from "helpers/enums";

interface IProps {
    parentCustomerID: string,
    parentSiteID: string,
    permissions?: IQuotePermissions,
    [x: string] : any  // this should always be the last prop, to support any other props that will be passed along down the component chain
}

function SelectSalesperson ({ parentCustomerID, parentSiteID, permissions, ...rest }: IProps)
{

    const tm = useTranslations();
    
    const salespersonList = useSalespersons(parentCustomerID, parentSiteID);
    const { quoteDefaults: addQuoteDefaults } = useAddQuoteData();
    const { quote, requiredFields } = useQuoteData();

    const inactiveTranslation = useMemo(() => {
        return "<" + tm.Get("Inactive") + ">";
    }, [tm]);

    const quoteDefaultSalespersonID = useMemo(() => {
        return (addQuoteDefaults ?? quote)?.salespersonID;
    }, [addQuoteDefaults, quote]);

    const isRequired = useMemo(() => {
        if (requiredFields) {
            if (requiredFields.includes(QuoteRequiredFieldEnum.Salesperson)) {
                return true;
            }
        }
        return false;
    }, [requiredFields]);

    const salesPersonIsValid = useCallback((value: any) => {
        if (isRequired) {
            //The standard require rule checks for null, but we treat salesPersonID === 0 as empty in the form. Need custom validation
            return value !== 0 || tm.GetWithParams("{0} cannot be blank.", tm.Get("Salesperson"));
        }

        return true;
    }, [tm, isRequired]);

    const { control, formState: { errors } } = useFormContext();
    const { field } = useController({
        name: "salespersonID", 
        control,
        rules: {
            validate: { isValid: salesPersonIsValid }
        },
    });

    const filteredSalesPersonList = useMemo(() => {
        if (salespersonList === undefined) {
            return undefined;
        }
        let filteredList: IEmployee[] = [];
        salespersonList.forEach(sp => {
            if (!sp.inactive || sp.employeeID === quoteDefaultSalespersonID) {
                filteredList.push(sp);
            }
        });
        return filteredList;
    }, [quoteDefaultSalespersonID, salespersonList]);

    const selectedSalesperson = useMemo(() => {
        return filteredSalesPersonList?.find((sp) => field.value && sp.employeeID === field.value) ?? null
    }, [filteredSalesPersonList, field]);

    return <>
        <Autocomplete 
            id="salesperson"
            autoComplete
            autoSelect
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            options={filteredSalesPersonList ?? []} 
            getOptionLabel={(option) => (option?.inactive ? inactiveTranslation : "") + (option?.fullName || "")}
            disabled={permissions ? !permissions.salesperson : true}
            renderInput={(params) => (
                <TextField 
                    variant="outlined" 
                    {...params} 
                    label={`${isRequired ? "*" : ""}${tm.Get("Salesperson")}`} 
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                            {(filteredSalesPersonList === undefined) && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                    error={(errors?.salespersonID?.message) ? true : false}
                    helperText={errors?.salespersonID?.message}                       
                />
            )}
            onChange = {(_, value) => {
                field.onChange(value?.employeeID ?? 0);
            }}
            value = {selectedSalesperson}   
            ref = {field.ref}
            {...rest}
        /> 
    </>

}

export default SelectSalesperson;